import { useQuery } from "@tanstack/react-query";
import { Bell, BookOpen, ChevronDown, LogIn, LogOut, Search, Settings, User } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { BrandSwitcher } from "./BrandSwitcher";
import { FIELD_LABELS, type Job } from "../lib/api";
import { signInUrl } from "../lib/http";
import { atLeast, initials, signOut, useMe, type Me } from "../lib/me";
import { fetchReviews, notificationsFor, REVIEWS_KEY, type Notifications } from "../pages/reviewFlow";
import "../theme/review.css";

/** The signed-in person's name and role, as the server resolved them. */
export function UserBadge({ me }: { me: Me }) {
  const name = me.name || me.subject || "Signed in";
  return (
    <>
      <div className="avatar" aria-hidden="true">{initials(name)}</div>
      <span>{name}</span>
      <span className={`role-badge role-${me.role}`}>{me.role}</span>
    </>
  );
}

const REASON_WORDS: Record<string, string> = {
  conflict: "documents disagree", no_box: "value not marked on the page", low_confidence: "low confidence", gate: "a run waiting at a step", needs_review: "a run needs a person",
};

/** The bell. It says how many things wait, and whether its list is open. */
export function BellButton({ count, open, onToggle, buttonRef }: {
  count: number; open: boolean; onToggle: () => void; buttonRef?: React.Ref<HTMLButtonElement>;
}) {
  return (
    <button ref={buttonRef} type="button" className="iconbtn" onClick={onToggle}
            aria-expanded={open} aria-controls="notifications"
            aria-label={count ? `Notifications, ${count} waiting` : "Notifications"}>
      <Bell size={18} aria-hidden="true" />
      {count > 0 && <span className="badge" aria-hidden="true">{count}</span>}
    </button>
  );
}

/**
 * What the bell opens: the review queue first, then the UC1 jobs only a person can move. Its
 * entries are ordinary links and buttons, reached with Tab; Escape closes it.
 */
export function NotificationList({ notes, onOpenJob, onClose }: {
  notes: Notifications; onOpenJob: (j: Job) => void; onClose: () => void;
}) {
  const pending = notes.count - notes.jobs.length;
  return (
    <div className="dropdown" id="notifications" role="region" aria-label="Notifications"
         onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}>
      <div className="head">Awaiting human review</div>
      {notes.count === 0 && <div className="item"><div className="d">Nothing is waiting on you.</div></div>}
      {notes.reviews.length > 0 && <div className="group">Review queue</div>}
      {notes.reviews.slice(0, 5).map((r) => (
        <Link key={r.id} className="item" to={`/review/${r.id}`} onClick={onClose}>
          <div className="t">
            {r.tag ?? "Item"}{r.field ? `: ${FIELD_LABELS[r.field] ?? r.field}` : ""}
          </div>
          <div className="d">{REASON_WORDS[r.reason] ?? r.reason}{r.package ? ` in the ${r.package} package` : ""}</div>
        </Link>
      ))}
      {pending > 0 && (
        <Link className="item" to="/review" onClick={onClose}>
          <div className="d">{pending} waiting in the review queue. Open the queue</div>
        </Link>
      )}
      {notes.jobs.length > 0 && <div className="group">Extraction jobs</div>}
      {notes.jobs.slice(0, 5).map((j) => (
        <button type="button" key={j.id} className="item"
                onClick={() => { onOpenJob(j); onClose(); }}>
          <span className="t">
            {j.status === "failed" ? `Tag not located: ${j.tag}`
              : j.status === "awaiting_confirmation" ? `Confirm the source document for ${j.tag}`
              : `Review extracted values for ${j.tag}`}
          </span>
          <span className="d">Job J-{j.id.slice(0, 4).toUpperCase()} in the {j.package} package</span>
        </button>
      ))}
    </div>
  );
}

/** Which menu item an arrow, Home or End key moves to from `index`; null for any other key. */
export function menuKeyTarget(key: string, index: number, count: number): number | null {
  if (count === 0) return null;
  if (key === "ArrowDown") return (index + 1) % count;
  if (key === "ArrowUp") return (index - 1 + count) % count;
  if (key === "Home") return 0;
  if (key === "End") return count - 1;
  return null;
}

/**
 * The account menu: who is signed in and with which packages, then Guide, Settings for an admin,
 * and Sign out. Each action is a real button or link, so Tab and Enter reach it; the arrow keys
 * move between them, and Escape closes the menu and gives focus back to the account button.
 */
export function UserMenu({ me, onGuide, onClose, onSignOut }: {
  me: Me; onGuide: () => void; onClose: () => void; onSignOut: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => { ref.current?.querySelector<HTMLElement>("[role=menuitem]")?.focus(); }, []);
  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape") { e.preventDefault(); onClose(); return; }
    const items = [...(ref.current?.querySelectorAll<HTMLElement>("[role=menuitem]") ?? [])];
    const next = menuKeyTarget(e.key, items.indexOf(document.activeElement as HTMLElement), items.length);
    if (next !== null) { e.preventDefault(); items[next].focus(); }
  }
  return (
    <div className="menu" role="menu" aria-label="Account" ref={ref} onKeyDown={onKeyDown}>
      <div className="menu-head" role="none">
        <span className="row" style={{ gap: 8 }}><User size={16} aria-hidden="true" /> {me.subject}</span>
        <span className="muted" style={{ fontSize: 13 }}>
          {me.packages === null ? "All packages" : me.packages.length ? me.packages.join(", ") : "No packages yet"}
        </span>
      </div>
      <div className="sep" role="none" />
      <button type="button" className="row-item" role="menuitem" onClick={() => { onGuide(); onClose(); }}>
        <BookOpen size={16} aria-hidden="true" /> Guide
      </button>
      {atLeast(me, "admin") && (
        <Link className="row-item" role="menuitem" to="/admin/settings" onClick={onClose}>
          <Settings size={16} aria-hidden="true" /> Settings
        </Link>
      )}
      <div className="sep" role="none" />
      <button type="button" className="row-item" role="menuitem" onClick={onSignOut}>
        <LogOut size={16} aria-hidden="true" /> Sign out
      </button>
    </div>
  );
}

export function TopBar({
  jobs, onOpen, onGuide, query, setQuery,
}: {
  jobs: Job[]; onOpen: (j: Job) => void; onGuide: () => void;
  query: string; setQuery: (v: string) => void;
}) {
  const [notif, setNotif] = useState(false);
  const [profile, setProfile] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const bell = useRef<HTMLButtonElement>(null);
  const { me, error } = useMe();
  // The pending queue drives the bell. Shared with the Review page's pending view, so one fetch.
  const queue = useQuery({
    queryKey: [...REVIEWS_KEY, "pending"], queryFn: () => fetchReviews("pending"),
    enabled: !!me, refetchInterval: 15_000, retry: false,
  });
  const notes = notificationsFor(jobs, queue.data ?? null);

  return (
    <header className="topbar">
      <div className="searchbox" style={{ marginRight: "auto", marginBottom: 0, width: 340 }}>
        <Search />
        <input value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Search jobs, tags and documents"
               placeholder="Search jobs, tags, documents..." />
      </div>

      <BrandSwitcher />

      <button className="iconbtn" onClick={onGuide} aria-label="Guide" title="Guide">
        <BookOpen size={18} />
      </button>

      <span onKeyDown={(e) => { if (e.key === "Escape" && notif) setNotif(false); }}>
        <BellButton count={notes.count} open={notif} buttonRef={bell}
                    onToggle={() => { setNotif(!notif); setProfile(false); }} />
        {/* Right after the bell, so the next Tab enters the list. */}
        {notif && <NotificationList notes={notes} onOpenJob={onOpen} onClose={() => { setNotif(false); bell.current?.focus(); }} />}
      </span>

      {me ? (
        <button ref={trigger} className="usermenu ghost" aria-haspopup="menu" aria-expanded={profile}
                aria-label={`Account: ${me.name || me.subject}, ${me.role}`}
                onKeyDown={(e) => { if (e.key === "Escape" && profile) setProfile(false); }}
                onClick={() => { setProfile(!profile); setNotif(false); }}>
          <UserBadge me={me} />
          <ChevronDown size={16} className="muted" />
        </button>
      ) : me === null ? (
        <a className="btn" href={signInUrl()}>
          <LogIn size={16} /> Sign in
        </a>
      ) : (
        <span className="muted" style={{ fontSize: 14 }}>
          {error ? "Sign-in could not be checked" : "Checking sign-in..."}
        </span>
      )}


      {profile && me && (
        <UserMenu me={me} onGuide={onGuide} onSignOut={() => { void signOut(); }}
                  onClose={() => { setProfile(false); trigger.current?.focus(); }} />
      )}
    </header>
  );
}
